import { useState, type FormEvent } from 'react';

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9q9chnnahb7382ov84g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <section id="contact" className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
            联系我们
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            有任何问题、建议或合作意向，欢迎随时联系我们
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 flex flex-col gap-6">
            <div className="bg-background-100 rounded-xl p-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                <i className="ri-mail-line text-xl text-primary-600"></i>
              </div>
              <h3 className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</h3>
              <p className="text-sm text-foreground-600">contact@chongshengluolidao.com</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                <i className="ri-discord-line text-xl text-accent-600"></i>
              </div>
              <h3 className="font-medium text-foreground-900 text-sm mb-1">社区频道</h3>
              <p className="text-sm text-foreground-600">加入我们的Discord社区，与冒险者们交流</p>
            </div>
            <div className="bg-background-100 rounded-xl p-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 mb-3">
                <i className="ri-time-line text-xl text-secondary-600"></i>
              </div>
              <h3 className="font-medium text-foreground-900 text-sm mb-1">工作时间</h3>
              <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-xl p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你想说的话（最多500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="bot-field"
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <p className="mt-4 text-sm text-accent-600 text-center">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="mt-4 text-sm text-red-500 text-center">{formMessage}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}